import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import {
  Skeleton,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Avatar,
  AvatarFallback,
  AvatarImage,
  useToast,
  Switch,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
  Textarea,
} from '@org/ui';
import {
  UserX,
  Plus,
  Trash2,
  Clock,
  Ban,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useState } from 'react';
import { formatDistanceToNow, format } from 'date-fns';

export const Route = createFileRoute('/admin/bans')({
  component: BannedUsersPage,
});

function BannedUsersPage() {
  const { t } = useTranslation('community');
  const { toast } = useToast();
  const [page, setPage] = useState(0);
  const [includeExpired, setIncludeExpired] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [showUnbanModal, setShowUnbanModal] = useState<string | null>(null);

  const limit = 20;

  const { data, isLoading, refetch } = communityTrpc.moderation.listBannedUsers.useQuery({
    includeExpired,
    limit,
    offset: page * limit,
  });

  const unbanUser = communityTrpc.moderation.unbanUser.useMutation({
    onSuccess: (result) => {
      toast({
        title: t('admin.bans.userUnbanned'),
        description: t('admin.bans.removedBans', { count: result.removedBans }),
      });
      setShowUnbanModal(null);
      refetch();
    },
    onError: (error) => {
      toast({ title: t('common.error'), description: error.message, variant: 'destructive' });
    },
  });

  const bans = data?.bans ?? [];
  const totalPages = Math.ceil((data?.total ?? 0) / limit);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('admin.bans.title')}</h1>
          <p className="text-muted-foreground">{t('admin.bans.subtitle')}</p>
        </div>
        <Button onClick={() => setShowBanModal(true)}>
          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('admin.bans.banUser')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Switch
            id="includeExpired"
            checked={includeExpired}
            onCheckedChange={setIncludeExpired}
          />
          <Label htmlFor="includeExpired" className="cursor-pointer">
            {t('admin.bans.showExpired')}
          </Label>
        </div>
      </div>

      {/* Bans List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <UserX className="h-5 w-5" aria-hidden="true" />
              {t('admin.bans.title')}
            </span>
            <span className="text-sm font-normal text-muted-foreground">
              {t('admin.bans.total', { count: data?.total ?? 0 })}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : bans.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <UserX className="mx-auto h-12 w-12 opacity-50" aria-hidden="true" />
              <p className="mt-2">{t('admin.bans.noBannedUsers')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {bans.map((ban) => {
                const isExpired = ban.expiresAt && new Date(ban.expiresAt) < new Date();
                const isPermanent = !ban.expiresAt;

                return (
                  <div
                    key={ban.banId}
                    className={`rounded-lg border p-4 ${
                      isExpired ? 'border-dashed bg-muted/50 opacity-75' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={ban.user?.profileImageUrl ?? undefined} />
                          <AvatarFallback>
                            {(ban.user?.displayName || ban.user?.firstName || 'U')[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">
                            {ban.user?.displayName ||
                             [ban.user?.firstName, ban.user?.lastName].filter(Boolean).join(' ') ||
                             'Unknown User'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {ban.user?.email}
                          </p>
                          <p className="mt-1 text-sm">
                            <span className="text-muted-foreground">{t('admin.bans.reason')}</span>{' '}
                            {ban.reason}
                          </p>
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span>
                              {t('admin.bans.bannedAgo')} {formatDistanceToNow(new Date(ban.createdAt), { addSuffix: true })}
                            </span>
                            {isPermanent ? (
                              <span className="rounded-full bg-red-100 px-2 py-0.5 font-medium text-red-700">
                                {t('admin.bans.permanent')}
                              </span>
                            ) : isExpired ? (
                              <span className="rounded-full bg-muted px-2 py-0.5 font-medium text-muted-foreground">
                                {t('admin.bans.expired')}
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-700">
                                <Clock className="h-3 w-3" aria-hidden="true" />
                                {t('admin.bans.expires')} {format(new Date(ban.expiresAt!), 'PPp')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {!isExpired && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowUnbanModal(ban.user?.userId ?? null)}
                          className="shrink-0"
                        >
                          <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                          {t('admin.bans.unban')}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                    {t('admin.bans.previous')}
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {t('admin.bans.page', { page: page + 1, total: totalPages })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= totalPages - 1}
                  >
                    {t('admin.bans.next')}
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ban User Modal */}
      <BanUserModal
        isOpen={showBanModal}
        onClose={() => setShowBanModal(false)}
        onSuccess={() => {
          setShowBanModal(false);
          refetch();
        }}
      />

      {/* Unban Confirmation Modal */}
      {showUnbanModal && (
        <Dialog open={true} onOpenChange={() => setShowUnbanModal(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('admin.bans.confirmUnban')}</DialogTitle>
              <DialogDescription>
                {t('admin.bans.unbanMessage')}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUnbanModal(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => unbanUser.mutate({ userId: showUnbanModal })}
                disabled={unbanUser.isPending}
              >
                {unbanUser.isPending ? t('admin.bans.processing') : t('admin.bans.unbanUser')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function BanUserModal({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation('community');
  const { toast } = useToast();
  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState('');
  const [banDays, setBanDays] = useState<number | null>(null);

  const banUser = communityTrpc.moderation.banUser.useMutation({
    onSuccess: () => {
      toast({ title: t('admin.bans.userBannedSuccess') });
      resetForm();
      onSuccess();
    },
    onError: (error) => {
      toast({ title: t('common.error'), description: error.message, variant: 'destructive' });
    },
  });

  const resetForm = () => {
    setUserId('');
    setReason('');
    setBanDays(null);
  };

  const handleSubmit = () => {
    if (!userId.trim()) {
      toast({ title: t('admin.bans.userIdRequired'), variant: 'destructive' });
      return;
    }
    if (!reason.trim()) {
      toast({ title: t('admin.bans.reasonRequired'), variant: 'destructive' });
      return;
    }

    const expiresAt = banDays
      ? new Date(Date.now() + banDays * 24 * 60 * 60 * 1000).toISOString()
      : undefined;

    banUser.mutate({
      userId,
      reason,
      expiresAt,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-red-600" aria-hidden="true" />
            {t('admin.bans.banUser')}
          </DialogTitle>
          <DialogDescription>
            {t('admin.bans.banSubtitle')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <div className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              <span className="text-sm font-medium">
                {t('admin.bans.banWarning')}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="userId">{t('admin.bans.userIdLabel')} *</Label>
            <Input
              id="userId"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder={t('admin.bans.userIdPlaceholder')}
            />
            <p className="text-xs text-muted-foreground">
              {t('admin.bans.userIdHint')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">{t('admin.bans.reasonLabel')} *</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('admin.bans.reasonPlaceholder')}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="banDays">{t('admin.bans.durationLabel')}</Label>
            <Input
              id="banDays"
              type="number"
              min={1}
              value={banDays ?? ''}
              onChange={(e) =>
                setBanDays(e.target.value ? parseInt(e.target.value) : null)
              }
              placeholder={t('admin.bans.durationHint')}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={banUser.isPending}
          >
            {banUser.isPending ? t('admin.bans.banning') : t('admin.bans.banUser')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
